/**
 * client/src/audio/sleepTimer.ts — the arithmetic of "stop reading to me in half an hour".
 *
 * Pure, so `test/sleep-timer.test.ts` can drive it without an engine. The engine owns the instance
 * and feeds it from `timeupdate`, never from `setTimeout`: the whole point of a sleep timer is that
 * the screen is off, and a locked iPhone runs no timers while `timeupdate` keeps firing for as long
 * as the audio plays. A timer counted on `setTimeout` would freeze at lock and fire at breakfast.
 *
 * ⚠️ **Counted in wall time between progress reports, with each step capped.** Media time is the
 * wrong clock (1.5× would end a "30 minute" timer after 20) and an uncapped wall-clock difference
 * charges a pause, a phone call or a frozen tab to the timer. `SLEEP_MAX_STEP_MS` is several
 * `timeupdate` intervals wide, so real playback is never under-counted and a gap is never believed.
 */

export type SleepChoice = 15 | 30 | 45 | 60 | "chapter";

/** The order the playback bar cycles through. Off is the step after the last one. */
export const SLEEP_CHOICES: readonly SleepChoice[] = [15, 30, 45, 60, "chapter"];

/** The fade before a minutes timer pauses, where the platform lets us set a volume at all. */
export const SLEEP_FADE_MS = 20_000;

/** The longest gap between two progress reports that is believed to be playback. */
export const SLEEP_MAX_STEP_MS = 2_000;

export type SleepState =
  | { kind: "off" }
  | { kind: "minutes"; totalMs: number; remainingMs: number }
  /** Stop when the narration crosses out of this chapter. */
  | { kind: "chapter"; chapterOrd: number };

export const SLEEP_OFF: SleepState = { kind: "off" };

export function armSleep(choice: SleepChoice, chapterOrd: number): SleepState {
  if (choice === "chapter") return { kind: "chapter", chapterOrd };
  const totalMs = choice * 60_000;
  return { kind: "minutes", totalMs, remainingMs: totalMs };
}

/** Charge `elapsedMs` of playback to the timer. A negative or absurd step counts as nothing / the cap. */
export function stepSleep(state: SleepState, elapsedMs: number): SleepState {
  if (state.kind !== "minutes") return state;
  const step = Number.isFinite(elapsedMs) && elapsedMs > 0 ? Math.min(elapsedMs, SLEEP_MAX_STEP_MS) : 0;
  return { ...state, remainingMs: Math.max(0, state.remainingMs - step) };
}

export function sleepExpired(state: SleepState): boolean {
  return state.kind === "minutes" && state.remainingMs <= 0;
}

/** 1 until the last `SLEEP_FADE_MS`, then linearly down to 0. */
export function sleepVolume(state: SleepState): number {
  if (state.kind !== "minutes" || state.remainingMs >= SLEEP_FADE_MS) return 1;
  return Math.max(0, state.remainingMs / SLEEP_FADE_MS);
}

/** Does a page in `chapterOrd` mean the chapter timer has done its job? */
export function chapterSleepDue(state: SleepState, chapterOrd: number): boolean {
  return state.kind === "chapter" && chapterOrd !== state.chapterOrd;
}

/**
 * The next choice for a one-tap cycling control: off → 15 → 30 → 45 → 60 → end of chapter → off.
 * `null` means "turn it off" — which is what makes the same button the cancel.
 */
export function nextSleepChoice(state: SleepState): SleepChoice | null {
  if (state.kind === "off") return SLEEP_CHOICES[0] ?? null;
  if (state.kind === "chapter") return null;
  const minutes = Math.round(state.totalMs / 60_000);
  const i = SLEEP_CHOICES.indexOf(minutes as SleepChoice);
  return i < 0 ? null : (SLEEP_CHOICES[i + 1] ?? null);
}

/** "14:32" — whole seconds, rounded up, so the label never reads 0:00 while it is still playing. */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** The short label a button wears. */
export function sleepLabel(state: SleepState): string {
  if (state.kind === "off") return "Sleep";
  if (state.kind === "chapter") return "Sleep: end of chapter";
  return `Sleep ${formatRemaining(state.remainingMs)}`;
}
